import { useSetAtom, useStore } from "jotai";
import { useEffect } from "react";

import {
  focusedGroupIdAtom,
  layoutAtom,
  layoutPickerOpenAtom,
  panelPickerOpenAtom,
  redoAtom,
  undoAtom,
} from "@/core/state";
import { usePermissions } from "@/core/auth/usePermissions";
import {
  findActiveGroup,
  findNodeById,
  removePanel,
  setActiveTab,
  type TabGroup,
} from "@/shell/layout";
import { kioskRoleFor } from "@/shell/render/kioskRoles";

/**
 * Raccourcis clavier globaux du cockpit.
 *
 * Utilise les modifs `Ctrl+Alt+*` (cross-platform : Cmd+Alt sur Mac, Ctrl+Alt
 * sur Windows/Linux) pour eviter les conflits avec les raccourcis browser
 * (Cmd+W ferme l'onglet, Cmd+Tab switch d'app).
 *
 *   Cmd/Ctrl + Shift + P  →  ouvre le LayoutPickerModal (12 layouts squares)
 *   Cmd/Ctrl + Alt + Right →  cycle vers le tab suivant dans le group focused
 *   Cmd/Ctrl + Alt + Left  →  cycle vers le tab precedent
 *   Cmd/Ctrl + Alt + W     →  ferme le tab actif du group focused
 *   Cmd/Ctrl + Z           →  undo (sur le layout du workspace actif)
 *   Cmd/Ctrl + Y           →  redo
 *   Cmd/Ctrl + Shift + Z   →  redo (alias compatible Mac/Linux)
 *
 * "Focused group" = celui dont l'utilisateur a clique le dernier (cf
 * `focusedGroupIdAtom`). Fallback : premier group non-vide en DFS via
 * `findActiveGroup`.
 *
 * Les raccourcis sont ignores quand l'utilisateur tape dans un champ texte
 * (input / textarea / contenteditable).
 *
 * No-op pour un CHATTEUR (décideur) : son cockpit est un kiosk verrouillé
 * (layout override fixe + lock de TabStrip) — fermer/cycler/ajouter un onglet
 * ou ouvrir le layout picker sont exactement les échappatoires que le kiosk
 * retire de l'UI. Sans ce garde, les MÊMES actions resteraient joignables au
 * clavier.
 * Le hook reste appelé inconditionnellement (règle des hooks) ; on sort tôt
 * à l'intérieur de l'effect.
 */
export function useKeyboardShortcuts() {
  const setPanelPickerOpen = useSetAtom(panelPickerOpenAtom);
  const setLayoutPickerOpen = useSetAtom(layoutPickerOpenAtom);
  const undo = useSetAtom(undoAtom);
  const redo = useSetAtom(redoAtom);
  const store = useStore();
  const { roleSlug, permissions } = usePermissions();
  const inKiosk = kioskRoleFor(roleSlug, permissions) !== null;

  useEffect(() => {
    if (inKiosk) return;

    function handler(e: KeyboardEvent) {
      // F11 → vrai plein écran. Traité AVANT le garde `isTypingTarget` (le
      // plein écran doit marcher même avec le focus dans un champ) et sans
      // modificateur. En Tauri, `toggleMaximize` laisse la barre des tâches
      // Windows visible — `setFullscreen` la masque (vrai plein écran).
      // `e.repeat` ignoré : maintenir F11 ne doit pas faire clignoter le mode.
      if (
        e.key === "F11" &&
        !e.repeat &&
        !e.shiftKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.metaKey
      ) {
        e.preventDefault();
        void toggleFullscreen();
        return;
      }

      if (isTypingTarget(e.target)) return;
      const cmd = e.ctrlKey || e.metaKey;

      // Cmd/Ctrl + Shift + Z  (redo, alias avant Z) — verifie en premier
      // car key.toLowerCase() === "z" matche aussi Cmd+Z plus bas.
      if (cmd && e.shiftKey && e.key.toLowerCase() === "z") {
        e.preventDefault();
        redo();
        return;
      }

      // Cmd/Ctrl + Z  (undo)
      if (cmd && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "z") {
        e.preventDefault();
        undo();
        return;
      }

      // Cmd/Ctrl + Y  (redo, alias windows/linux)
      if (cmd && e.key.toLowerCase() === "y") {
        e.preventDefault();
        redo();
        return;
      }

      // Cmd/Ctrl + Shift + P  →  layout picker (les 12 carres)
      if (cmd && e.shiftKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        setLayoutPickerOpen((o) => !o);
        return;
      }

      // Cmd/Ctrl + Alt + Right
      if (cmd && e.altKey && e.key === "ArrowRight") {
        e.preventDefault();
        cycleTab(store, +1);
        return;
      }

      // Cmd/Ctrl + Alt + Left
      if (cmd && e.altKey && e.key === "ArrowLeft") {
        e.preventDefault();
        cycleTab(store, -1);
        return;
      }

      // Cmd/Ctrl + Alt + W
      if (cmd && e.altKey && e.key.toLowerCase() === "w") {
        e.preventDefault();
        closeActiveTab(store);
        return;
      }

      // Escape : ferme les modals (les modals gerent leur propre listener
      // mais redondance OK)
      if (e.key === "Escape") {
        setPanelPickerOpen(false);
        setLayoutPickerOpen(false);
      }
    }

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setPanelPickerOpen, setLayoutPickerOpen, undo, redo, store, inKiosk]);
}

/* ============================================================ */
/* Internals                                                     */
/* ============================================================ */

/**
 * Bascule le vrai plein écran.
 *   - Tauri desktop : `setFullscreen` sur la fenêtre native → masque la barre
 *     des tâches de l'OS (ce que `maximize` ne fait pas).
 *   - Web / PWA : Fullscreen API sur `<html>`.
 * Les erreurs (geste utilisateur absent, déjà en plein écran…) sont avalées.
 */
async function toggleFullscreen(): Promise<void> {
  try {
    if (typeof window !== "undefined" && "__TAURI_INTERNALS__" in window) {
      const { getCurrentWindow } = await import("@tauri-apps/api/window");
      const win = getCurrentWindow();
      const isFs = await win.isFullscreen();
      await win.setFullscreen(!isFs);
      return;
    }
    if (typeof document === "undefined") return;
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await document.documentElement.requestFullscreen();
    }
  } catch {
    /* plein écran refusé / indisponible — no-op */
  }
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.isContentEditable
  );
}

type Store = ReturnType<typeof useStore>;

function getFocusedGroup(store: Store): TabGroup | null {
  const layout = store.get(layoutAtom);
  const focusedId = store.get(focusedGroupIdAtom);
  if (focusedId) {
    const node = findNodeById(layout, focusedId);
    if (node?.kind === "tabs") return node;
  }
  return findActiveGroup(layout);
}

function cycleTab(store: Store, delta: number): void {
  const grp = getFocusedGroup(store);
  if (!grp || grp.panels.length === 0) return;
  const newIdx =
    (grp.activeIndex + delta + grp.panels.length) % grp.panels.length;
  store.set(layoutAtom, (prev) => setActiveTab(prev, grp.id, newIdx));
}

function closeActiveTab(store: Store): void {
  const grp = getFocusedGroup(store);
  if (!grp || grp.panels.length === 0) return;
  const active = grp.panels[grp.activeIndex] ?? grp.panels[0];
  if (!active) return;
  store.set(layoutAtom, (prev) => removePanel(prev, active.id));
}
